import { useCallback } from 'react';

const MONTH_SCROLL_DIRECTIONS = {
  BACK: 'back',
  FORWARD: 'forward',
};

export const useCalendarDisplayOffsetOperations = ({
  monthListRef,
  monthWidthPx,
  monthSeparationPx,
}) => {
  const getScrollOffsetForMonth = useCallback(
    (monthIndex) => monthIndex * monthWidthPx + monthSeparationPx,
    [monthWidthPx, monthSeparationPx]
  );

  const getMonthIndexFromOffset = useCallback(
    (offset, move = null) => {
      const rawMonthFractionalIndex =
        (offset - monthSeparationPx) / monthWidthPx;
      const currentMonthToleranceFraction = 0.1;

      const nearestMinMonth = Math.floor(rawMonthFractionalIndex);
      const nearestMaxMonth = nearestMinMonth + 1;

      const nearestMinMonthDistance = rawMonthFractionalIndex - nearestMinMonth;
      const nearestMaxMonthDistance = rawMonthFractionalIndex - nearestMaxMonth;

      if (
        Math.abs(nearestMinMonthDistance) < Math.abs(nearestMaxMonthDistance)
      ) {
        const withinTolerance =
          Math.abs(nearestMinMonthDistance) < currentMonthToleranceFraction;
        if (move === MONTH_SCROLL_DIRECTIONS.BACK) {
          if (withinTolerance) {
            return nearestMinMonth - 1;
          }
          return nearestMinMonth;
        }

        if (move === MONTH_SCROLL_DIRECTIONS.FORWARD) {
          return nearestMaxMonth;
        }
        return nearestMinMonth;
      }

      const withinTolerance =
        Math.abs(nearestMaxMonthDistance) < currentMonthToleranceFraction;
      if (move === MONTH_SCROLL_DIRECTIONS.FORWARD) {
        if (withinTolerance) {
          return nearestMaxMonth + 1;
        }
      }

      if (move === MONTH_SCROLL_DIRECTIONS.BACK) {
        return nearestMinMonth;
      }
      return nearestMaxMonth;
    },
    [monthSeparationPx, monthWidthPx]
  );

  const getScrollContainerRef = useCallback(
    // eslint-disable-next-line no-underscore-dangle
    () => monthListRef?.current?._outerRef,
    [monthListRef]
  );
  const scrollWithAnimation = useCallback(
    (x) => {
      getScrollContainerRef()?.scroll({ left: x, top: 0, behavior: 'smooth' });
    },
    [getScrollContainerRef]
  );

  const scrollToMonth = useCallback(
    (monthIndex) => scrollWithAnimation(getScrollOffsetForMonth(monthIndex)),
    [scrollWithAnimation, getScrollOffsetForMonth]
  );

  const scrollToNextMonth = useCallback(
    (move) => {
      const offset = getScrollContainerRef()?.scrollLeft;
      if (offset === undefined) return;
      const newMonth = getMonthIndexFromOffset(offset, move);
      scrollToMonth(newMonth);
    },
    [getScrollContainerRef, scrollToMonth, getMonthIndexFromOffset]
  );

  const onGoToNextMonth = useCallback(
    () => scrollToNextMonth(MONTH_SCROLL_DIRECTIONS.FORWARD),
    [scrollToNextMonth]
  );
  const onGoToPrevMonth = useCallback(
    () => scrollToNextMonth(MONTH_SCROLL_DIRECTIONS.BACK),
    [scrollToNextMonth]
  );

  return {
    onGoToNextMonth,
    onGoToPrevMonth,
    scrollToMonth,
    getScrollOffsetForMonth,
  };
};
